// The single source of truth for what the toolbar shows (feature 003).
//
// The snapshot is DERIVED from the live editor every time its state changes —
// there is no separately tracked "what the user last pressed" record that could
// drift from reality (spec FR-014). `useEditorState` recomputes the snapshot per
// transaction and, by its deep-equality default, only re-renders the toolbar
// when a flag actually flips (research D-02), so this keeps up with the cursor
// well inside the perceptual budget (SC-003).

import { useEditorState, type Editor } from '@tiptap/react';
import { FORMATTING_CONTROLS, type FormattingControlId } from './controls.js';

export interface ControlState {
  /** The format is in effect across the whole current selection / at the cursor. */
  active: boolean;
  /** The command can be applied here. False also while the editor is not ready. */
  available: boolean;
}

export type FormattingSnapshot = Record<FormattingControlId, ControlState>;

// Pure, hook-free derivation so it can be unit-tested against a real editor and
// reused by the hook. When there is no editor yet, every control reads inactive
// and unavailable (FR-009). `available` is the general `can()` check, so a
// control disables itself automatically wherever its command cannot run.
export function computeFormattingState(editor: Editor | null | undefined): FormattingSnapshot {
  const snapshot = {} as Record<FormattingControlId, ControlState>;
  for (const control of FORMATTING_CONTROLS) {
    snapshot[control.id] = {
      active: editor ? control.isActive(editor) : false,
      available: editor ? control.can(editor) : false,
    };
  }
  return snapshot;
}

export function useFormattingState(editor: Editor | undefined): FormattingSnapshot {
  const snapshot = useEditorState({
    editor: editor ?? null,
    selector: ({ editor: current }) => computeFormattingState(current),
  });
  // `useEditorState` yields null when there is no editor; fall back to the
  // all-inactive snapshot so the toolbar always has a complete reading.
  return snapshot ?? computeFormattingState(undefined);
}
